import React, { useEffect, useState } from 'react';
import { 
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  ShoppingCart,
  Users,
  Building,
  Truck,
  Package,
  Download,
  Calendar,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AnalyticsAPI, DashboardAnalytics } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function AnalyticsDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsAPI.getDashboardAnalytics({ period });
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchOrderDetails = async (orderId: string) => {
    try {
      setLoadingOrderDetails(true);
      const response = await AnalyticsAPI.getOrderDetails(orderId);
      if (response.success) {
        setOrderDetails(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching order details:', err);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleOrderClick = (orderId: string) => {
    setSelectedOrder(orderId);
    fetchOrderDetails(orderId);
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setOrderDetails(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load analytics'}</p>
          <Button onClick={fetchAnalytics}>Retry</Button>
        </div>
      </div>
    );
  }

  const { overview, trends } = analytics;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-muted-foreground">Comprehensive business insights and metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <select 
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
          >
            <option value="day">Last 24 hours</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="year">Last year</option>
          </select>
        </div>
      </div>
      
      {/* Key metrics */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/analytics/revenue')}>
          <CardContent className="p-6">
            <div className="flex flex-row items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(overview.revenue.totalRevenue)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/analytics/orders')}>
          <CardContent className="p-6">
            <div className="flex flex-row items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
                <p className="text-2xl font-bold">{overview.orders.totalOrders}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Avg: {formatCurrency(overview.orders.avgOrderValue)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/analytics/users')}>
          <CardContent className="p-6">
            <div className="flex flex-row items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{overview.users.totalUsers}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {overview.users.verifiedUsers} verified
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/analytics/suppliers')}>
          <CardContent className="p-6">
            <div className="flex flex-row items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Suppliers</p>
                <p className="text-2xl font-bold">{overview.suppliers.activeSuppliers}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {overview.suppliers.verifiedSuppliers} verified
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Building className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Revenue trends chart */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Trends</CardTitle>
          <CardDescription>Daily revenue and order count</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]">
            {overview.revenue.trends.length > 0 ? (
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-primary mr-2"></div>
                      <span className="text-sm">Revenue</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-400 mr-2"></div>
                      <span className="text-sm">Orders</span>
                    </div>
                  </div>
                  <div className="text-2xl font-bold">{formatCurrency(overview.revenue.totalRevenue)}</div>
                </div>
                
                <div className="flex-1 flex items-end gap-1">
                  {overview.revenue.trends.slice(-10).map((trend) => {
                    const maxRevenue = Math.max(...overview.revenue.trends.slice(-10).map(t => t.revenue));
                    const maxOrders = Math.max(...overview.revenue.trends.slice(-10).map(t => t.orderCount));
                    const revenueHeight = maxRevenue > 0 ? (trend.revenue / maxRevenue) * 100 : 0;
                    const ordersHeight = maxOrders > 0 ? (trend.orderCount / maxOrders) * 100 : 0;
                    const date = new Date(trend.date);
                    const label = `${date.getMonth() + 1}/${date.getDate()}`;
                    
                    return (
                      <div key={trend.date} className="relative flex-1 flex flex-col items-center group">
                        <div className="w-full flex flex-col items-center" style={{ height: '250px' }}>
                          <div className="flex-1 w-full flex items-end justify-center gap-1">
                            <div
                              className="flex-1 bg-primary rounded-t-sm transition-all hover:opacity-80 cursor-pointer"
                              style={{ height: `${revenueHeight}%`, minHeight: revenueHeight > 0 ? '4px' : '0' }}
                            ></div>
                            <div
                              className="flex-1 bg-blue-400 rounded-t-sm transition-all hover:opacity-80 cursor-pointer"
                              style={{ height: `${ordersHeight}%`, minHeight: ordersHeight > 0 ? '4px' : '0' }}
                            ></div>
                          </div>
                        </div>
                        <span className="mt-2 text-xs text-center">{label}</span>
                        <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover text-popover-foreground px-3 py-2 rounded text-xs whitespace-nowrap shadow-lg border z-10">
                          <div className="font-medium">{date.toLocaleDateString()}</div>
                          <div className="text-primary">{formatCurrency(trend.revenue)}</div>
                          <div className="text-muted-foreground">{trend.orderCount} orders</div>
                          <div className="text-muted-foreground">Avg: {formatCurrency(trend.avgOrderValue)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No revenue data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Order status distribution and top suppliers */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Order Status Distribution</CardTitle>
            <CardDescription>Current order statuses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trends.orderStatusDistribution.map((item) => {
                const percentage = overview.orders.totalOrders > 0 
                  ? (item.count / overview.orders.totalOrders) * 100 
                  : 0;
                
                return (
                  <div key={item.status}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{item.status}</span>
                      <span className="text-sm text-muted-foreground">
                        {item.count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`rounded-full h-2 ${
                          item.status === 'delivered' ? 'bg-green-500' :
                          item.status === 'processing' ? 'bg-blue-500' :
                          item.status === 'pending' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Top Suppliers</CardTitle>
            <CardDescription>By order count and revenue</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trends.topSuppliers.slice(0, 5).map((supplier, index) => (
                <div key={supplier.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{supplier.name}</p>
                      <p className="text-xs text-muted-foreground">{supplier.orderCount} orders</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(supplier.totalValue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent orders */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
          <CardDescription>Latest order activities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {trends.recentOrders.slice(0, 10).map((order) => (
              <div 
                key={order._id} 
                className="flex items-center justify-between border-b pb-3 last:border-0 hover:bg-muted/50 p-2 rounded cursor-pointer transition-colors"
                onClick={() => handleOrderClick(order._id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">
                      {order.trader ? `${order.trader.firstName} ${order.trader.lastName}` : 'N/A'}
                    </p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <span>{order.orderNumber}</span>
                    {order.supplier && (
                      <>
                        <span>•</span>
                        <span>{order.supplier.businessName}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right flex items-center gap-2">
                  <div>
                    <p className="font-medium">{formatCurrency(order.totalAmount)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => navigate('/analytics/orders')}>
              View All Orders
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Additional stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Transporters</p>
              <Truck className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{overview.transporters.totalTransporters}</p>
            <div className="mt-2 text-sm">
              <span className="text-green-600">{overview.transporters.activeTransporters} active</span>
              <span className="text-muted-foreground"> • </span>
              <span className="text-muted-foreground">{overview.transporters.verifiedTransporters} verified</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Products</p>
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{overview.products.totalProducts}</p>
            <div className="mt-2 text-sm">
              <span className="text-green-600">{overview.products.productsWithOrders} with orders</span>
              <span className="text-muted-foreground"> • </span>
              <span className="text-muted-foreground">{overview.products.totalCategories} categories</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">User Engagement</p>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{overview.users.usersWithOrders}</p>
            <div className="mt-2 text-sm">
              <span className="text-muted-foreground">
                {overview.users.totalUsers > 0 
                  ? ((overview.users.usersWithOrders / overview.users.totalUsers) * 100).toFixed(1)
                  : 0}% of total users
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Details Modal */}
      <Dialog open={selectedOrder !== null} onOpenChange={closeOrderModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {loadingOrderDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : orderDetails ? (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order Number</p>
                  <p className="font-medium">{orderDetails.order.orderNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(orderDetails.order.status)}`}>
                    {orderDetails.order.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Order Date</p>
                  <p className="font-medium">{new Date(orderDetails.order.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="font-medium text-lg">{formatCurrency(orderDetails.order.totalAmount)}</p>
                </div>
              </div>

              {/* Customer Info */}
              {orderDetails.order.trader && (
                <div>
                  <h3 className="font-semibold mb-2">Customer Information</h3>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="font-medium">{orderDetails.order.trader.firstName} {orderDetails.order.trader.lastName}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.trader.phoneNumber}</p>
                    {orderDetails.order.trader.email && (
                      <p className="text-sm text-muted-foreground">{orderDetails.order.trader.email}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Supplier Info */}
              {orderDetails.order.assignedSupplier && (
                <div>
                  <h3 className="font-semibold mb-2">Supplier Information</h3>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="font-medium">{orderDetails.order.assignedSupplier.businessName}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.assignedSupplier.ownerName}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.assignedSupplier.mobile}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.assignedSupplier.email}</p>
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div>
                <h3 className="font-semibold mb-2">Order Items</h3>
                <div className="space-y-2">
                  {orderDetails.order.items.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-medium">{item.product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.quantity} {item.unit} × {formatCurrency(item.pricePerUnit)}
                        </p>
                      </div>
                      <p className="font-medium">{formatCurrency(item.totalPrice)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(orderDetails.order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span>{formatCurrency(orderDetails.order.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Delivery Charge</span>
                    <span>{formatCurrency(orderDetails.order.deliveryCharge)}</span>
                  </div>
                  {orderDetails.order.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-{formatCurrency(orderDetails.order.discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span>{formatCurrency(orderDetails.order.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
